/**
 * safePost / safePut / safeDelete — R53.31
 *
 * Wrapper attorno ad axios che:
 *  1. Se `navigator.onLine === false` → accoda l'operazione in IndexedDB
 *     e restituisce un risultato sintetico `{ offline: true, local_id }`.
 *  2. Tenta la chiamata: se fallisce per errore di rete (no response) →
 *     accoda e restituisce sintetico.
 *  3. Per qualsiasi altro errore (4xx/5xx) → propaga normalmente.
 *
 * Vantaggio: i form HACCP esistenti possono usarlo come drop-in di `api.post`
 * senza cambiare la UX. Il toast di "salvato" appare anche offline.
 */
import api from "@/lib/api";
import { enqueue } from "@/lib/offlineQueue";

function isNetworkError(err) {
  // axios: nessuna response → connection refused / DNS / offline
  if (!err) return false;
  if (err.response) return false;
  return true;
}

function shouldQueue() {
  if (typeof navigator === "undefined") return false;
  return navigator.onLine === false;
}

/**
 * @param {string} url
 * @param {object} body
 * @param {object} opts - { label?: string, axiosConfig?: object }
 * @returns {Promise<{data:any} | {offline:true, local_id:string}>}
 */
export async function safePost(url, body, opts = {}) {
  const { label = "", axiosConfig = {} } = opts;
  if (shouldQueue()) {
    const op = await enqueue({ method: "POST", url, body, label });
    return { offline: true, local_id: op.local_id, data: null };
  }
  try {
    const resp = await api.post(url, body, axiosConfig);
    return resp;
  } catch (e) {
    if (isNetworkError(e)) {
      const op = await enqueue({ method: "POST", url, body, label });
      return { offline: true, local_id: op.local_id, data: null };
    }
    throw e;
  }
}

export async function safePut(url, body, opts = {}) {
  const { label = "", axiosConfig = {} } = opts;
  if (shouldQueue()) {
    const op = await enqueue({ method: "PUT", url, body, label });
    return { offline: true, local_id: op.local_id, data: null };
  }
  try {
    const resp = await api.put(url, body, axiosConfig);
    return resp;
  } catch (e) {
    if (isNetworkError(e)) {
      const op = await enqueue({ method: "PUT", url, body, label });
      return { offline: true, local_id: op.local_id, data: null };
    }
    throw e;
  }
}

export async function safeDelete(url, opts = {}) {
  const { label = "" } = opts;
  if (shouldQueue()) {
    const op = await enqueue({ method: "DELETE", url, body: null, label });
    return { offline: true, local_id: op.local_id, data: null };
  }
  try {
    const resp = await api.delete(url);
    return resp;
  } catch (e) {
    if (isNetworkError(e)) {
      const op = await enqueue({ method: "DELETE", url, body: null, label });
      return { offline: true, local_id: op.local_id, data: null };
    }
    throw e;
  }
}
